import {CardinalDirection, MaintenanceIntervalType} from "../../../types/Enums";

export interface DirectionalInterval {
    direction: CardinalDirection;
    interval_type: MaintenanceIntervalType;
    interval_value: number;
}

export interface MaintenanceTemplate {
    id: string;
    name: string;
    description: string;
    interval_type: MaintenanceIntervalType;
    interval_value: number;
    cost: number;
    cost_unit: 'per_sqm' | 'per_unit';
    directionalIntervals?: DirectionalInterval[];
}

export interface TemplatesByType {
    [key: string]: MaintenanceTemplate[];
}

export interface EditForm {
    name: string;
    description: string;
    interval_type: MaintenanceIntervalType;
    interval_value: number;
    cost: number;
    cost_unit: 'per_sqm' | 'per_unit';
    directionalIntervals: DirectionalInterval[];
}

export interface DirectionalLifetime {
    direction: CardinalDirection;
    expectedLifetimeYears: number;
    replacementCost?: number;
}

export interface ComponentLifetime {
    expectedLifetimeYears: number;
    replacementCost?: number;
    costUnit?: 'per_sqm' | 'per_unit';
    directionalLifetimes?: DirectionalLifetime[];
}

export function getLifetimeForDirection(
    lifetime: ComponentLifetime,
    direction: CardinalDirection | null
): { expectedLifetimeYears: number; replacementCost?: number } {
    if (!direction || !lifetime.directionalLifetimes) {
        return {
            expectedLifetimeYears: lifetime.expectedLifetimeYears,
            replacementCost: lifetime.replacementCost
        };
    }

    const directionalLifetime = lifetime.directionalLifetimes.find(
        dl => dl.direction === direction
    );

    return {
        expectedLifetimeYears: directionalLifetime?.expectedLifetimeYears || lifetime.expectedLifetimeYears,
        replacementCost: directionalLifetime?.replacementCost ?? lifetime.replacementCost
    };
}

export function getIntervalForDirection(
    template: MaintenanceTemplate,
    direction: CardinalDirection | null
): { interval_type: MaintenanceIntervalType; interval_value: number } {
    if (!direction || !template.directionalIntervals) {
        return {
            interval_type: template.interval_type,
            interval_value: template.interval_value
        };
    }

    const directionalInterval = template.directionalIntervals.find(
        di => di.direction === direction
    );

    return {
        interval_type: directionalInterval?.interval_type || template.interval_type,
        interval_value: directionalInterval?.interval_value || template.interval_value
    };
}

/**
 * Get the interval type for a template
 */
export function getIntervalTypeForTemplate(template: MaintenanceTemplate): MaintenanceIntervalType {
    return template.interval_type;
}

/**
 * Get the interval value for a template
 */
export function getIntervalValueForTemplate(template: MaintenanceTemplate): number {
    return template.interval_value;
}

/**
 * Get the interval type for a directional interval
 */
export function getIntervalTypeForDirection(
    template: MaintenanceTemplate,
    direction: CardinalDirection | null
): MaintenanceIntervalType {
    if (!direction || !template.directionalIntervals) {
        return getIntervalTypeForTemplate(template);
    }

    const directionalInterval = template.directionalIntervals.find(
        di => di.direction === direction
    );

    return directionalInterval?.interval_type || getIntervalTypeForTemplate(template);
}

/**
 * Get the interval value for a directional interval
 */
export function getIntervalValueForDirection(
    template: MaintenanceTemplate,
    direction: CardinalDirection | null
): number {
    if (!direction || !template.directionalIntervals) {
        return getIntervalValueForTemplate(template);
    }

    const directionalInterval = template.directionalIntervals.find(
        di => di.direction === direction
    );

    return directionalInterval?.interval_value || getIntervalValueForTemplate(template);
}
